import * as React from 'react';

import { useHorizontalScrollOverflow } from './hooks/use-horizontal-scroll-overflow';

import { cn } from '@/lib/utils';

export interface HorizontalScrollFadeIndicatorsProps {
  scrollRef: React.RefObject<HTMLElement | null>;
  fadeFromClassName?: string;
  className?: string;
}

function HorizontalScrollFadeIndicators({
  scrollRef,
  fadeFromClassName = 'from-surface-base',
  className,
}: HorizontalScrollFadeIndicatorsProps) {
  const { canScrollLeft, canScrollRight } = useHorizontalScrollOverflow(scrollRef);

  return (
    <div
      aria-hidden="true"
      className={cn('pointer-events-none absolute inset-0 z-10', className)}
      data-slot="horizontal-scroll-fade-indicators"
    >
      <div
        className={cn(
          'absolute inset-y-0 left-0 w-6 bg-gradient-to-r to-transparent transition-opacity duration-fast ease-standard motion-reduce:transition-none',
          fadeFromClassName,
          canScrollLeft ? 'opacity-100' : 'opacity-0'
        )}
        data-edge="left"
      />
      <div
        className={cn(
          'absolute inset-y-0 right-0 w-6 bg-gradient-to-l to-transparent transition-opacity duration-fast ease-standard motion-reduce:transition-none',
          fadeFromClassName,
          canScrollRight ? 'opacity-100' : 'opacity-0'
        )}
        data-edge="right"
      />
    </div>
  );
}

export interface HorizontalScrollFadeProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  fadeFromClassName?: string;
  viewportClassName?: string;
  ref?: React.Ref<HTMLDivElement>;
}

function HorizontalScrollFade({
  children,
  className,
  fadeFromClassName = 'from-surface-base',
  viewportClassName,
  ref,
  ...props
}: HorizontalScrollFadeProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  return (
    <div
      {...props}
      ref={ref}
      className={cn('relative min-w-0', className)}
      data-slot="horizontal-scroll-fade"
    >
      <div
        ref={scrollRef}
        className={cn('overflow-x-auto scrollbar-none', viewportClassName)}
        data-slot="horizontal-scroll-fade-viewport"
      >
        {children}
      </div>
      <HorizontalScrollFadeIndicators
        fadeFromClassName={fadeFromClassName}
        scrollRef={scrollRef}
      />
    </div>
  );
}

export { HorizontalScrollFade, HorizontalScrollFadeIndicators };
